/**
 * Auth Navigator
 * Navigation stack for authentication screens
 * Landing page is the initial screen for non-authenticated users
 */

import React from 'react';
import { createStackNavigator } from '@react-navigation/stack';
import useDomain from '../hooks/useDomain';
import HomeScreen from '../screens/pages/HomeScreen';
import YoungProfessionalLandingScreen from '../screens/pages/YoungProfessionalLandingScreen';
import EarlySaverLandingScreen from '../screens/pages/EarlySaverLandingScreen';
import TermsOfServiceScreen from '../screens/pages/TermsOfServiceScreen';
import PrivacyPolicyScreen from '../screens/pages/PrivacyPolicyScreen';
import LegalNoticesScreen from '../screens/pages/LegalNoticesScreen';
import ToolsListScreen from '../screens/tools/ToolsListScreen';
import CompoundInterestScreen from '../screens/tools/CompoundInterestScreen';
import LoginScreen from '../components/auth/LoginScreen';
import SignUpScreen from '../components/auth/SignUpScreen';
import ForgotPasswordScreen from '../components/auth/ForgotPasswordScreen';

export type AuthStackParamList = {
  Landing: undefined;
  'fr/': undefined;
  'fr/young-professional': undefined;
  'fr/pages/young-professional': undefined;
  'fr/early-saver': undefined;
  'fr/pages/early-saver': undefined;
  'fr/terms': undefined;
  'fr/privacy': undefined;
  'fr/legal': undefined;
  'fr/tools': undefined;
  'fr/tools/compound-interest-calculator': undefined;
  Login: undefined;
  SignUp: undefined;
  ForgotPassword: undefined;
};

const Stack = createStackNavigator<AuthStackParamList>();

export default function AuthNavigator() {
  const { isAppDomain } = useDomain();

  // On app.wealfi.co / app-staging.wealfi.co, non-authenticated users
  // should see the login screen only — no landing pages (avoids duplicate
  // content and ensures deep links like /fr/young-professional redirect
  // to /login because unmatched deep links fall back to initialRouteName).
  const initialRouteName = isAppDomain ? 'Login' : 'Landing';

  return (
    <Stack.Navigator
      initialRouteName={initialRouteName}
      screenOptions={{
        headerShown: false,
        cardStyle: { backgroundColor: '#FFFFFF' },
      }}
    >
      {/* Landing / marketing pages — only on wealfi.co, not app.wealfi.co */}
      {!isAppDomain && (
        <>
          <Stack.Screen
            name="Landing"
            component={HomeScreen}
            options={{ title: 'Wealfi - Ton assistant financier IA' }}
          />
          <Stack.Screen
            name="fr/"
            component={HomeScreen}
            options={{ title: 'Wealfi - Ton assistant financier IA' }}
          />
          <Stack.Screen
            name="fr/young-professional"
            component={YoungProfessionalLandingScreen}
            options={{ title: 'Wealfi - Jeune Professionnel' }}
          />
          <Stack.Screen
            name="fr/pages/young-professional"
            component={YoungProfessionalLandingScreen}
            options={{ title: 'Wealfi - Jeune Professionnel' }}
          />
          <Stack.Screen
            name="fr/pages/early-saver"
            component={EarlySaverLandingScreen}
            options={{ title: 'Wealfi - Premiers Pas' }}
          />
          <Stack.Screen
            name="fr/early-saver"
            component={EarlySaverLandingScreen}
            options={{ title: 'Wealfi - Premiers Pas' }}
          />
          <Stack.Screen
            name="fr/terms"
            component={TermsOfServiceScreen}
            options={{ title: 'CGU - Wealfi' }}
          />
          <Stack.Screen
            name="fr/privacy"
            component={PrivacyPolicyScreen}
            options={{ title: 'Politique de confidentialité - Wealfi' }}
          />
          <Stack.Screen
            name="fr/legal"
            component={LegalNoticesScreen}
            options={{ title: 'Mentions légales - Wealfi' }}
          />
          <Stack.Screen
            name="fr/tools"
            component={ToolsListScreen}
            options={{ title: 'Outils Financiers Gratuits - Wealfi' }}
          />
          <Stack.Screen
            name="fr/tools/compound-interest-calculator"
            component={CompoundInterestScreen}
            options={{ title: "Calculatrice Intérêts Composés - Wealfi" }}
          />
        </>
      )}

      {/* Auth screens — always available */}
      <Stack.Screen
        name="Login"
        component={LoginScreen}
        options={{ title: 'Connexion - Wealfi' }}
      />
      <Stack.Screen
        name="SignUp"
        component={SignUpScreen}
        options={{ title: 'Inscription - Wealfi' }}
      />
      <Stack.Screen
        name="ForgotPassword"
        component={ForgotPasswordScreen}
        options={{ title: 'Mot de passe oublie - Wealfi' }}
      />
    </Stack.Navigator>
  );
}

